import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Groups as GroupsIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as EmojiEventsIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  RadarChart as RadarChartRaw,
  Radar as RadarRaw,
  PolarGrid as PolarGridRaw,
  PolarAngleAxis as PolarAngleAxisRaw,
  PolarRadiusAxis as PolarRadiusAxisRaw,
  LineChart,
  Line,
} from 'recharts';
import { subDays, format } from 'date-fns';
import MainLayout from '../components/MainLayout';
import { apiService } from '../services/apiService';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useTutorial } from '../hooks/useTutorial';
import { useAppSelector } from '../hooks/redux';
import {
  RUBRIC_DIMENSIONS,
  RUBRIC_MAX_SCORE,
  BAND_COLORS,
} from '../constants/rubricDimensions';
import WeakAreasPanel from '../components/analytics/WeakAreasPanel';
import DimensionTrendGrid from '../components/analytics/DimensionTrendGrid';

// recharts v3 types some polar/radar components as returning `ReactNode`
// (which includes `undefined`), which TS rejects as a JSX element. Re-alias
// them to a JSX-safe component type. (The cartesian components above type fine.)
const RadarChart = RadarChartRaw as React.ComponentType<any>;
const Radar = RadarRaw as React.ComponentType<any>;
const PolarGrid = PolarGridRaw as React.ComponentType<any>;
const PolarAngleAxis = PolarAngleAxisRaw as React.ComponentType<any>;
const PolarRadiusAxis = PolarRadiusAxisRaw as React.ComponentType<any>;

// --- Data contracts (mirror backend /admin/statistics/rubric*) --------------
interface SessionStats {
  totalSessions: number;
  analyzedSessions: number;
  activeSalespeople: number;
  avgDurationSeconds: number;
  totalDurationSeconds: number;
  avgScorePercent: number | null;
}

interface SalespersonScore {
  userId: string;
  userEmail: string;
  sessionsAnalyzed: number;
  avgScorePercent: number | null;
  // Per rubric-dimension average on the native 1-5 scale, keyed by rubric key.
  dimensions: Record<string, number>;
}

interface HistoryPoint {
  sessionId: string;
  title: string | null;
  date: string | null;
  scorePercent: number | null;
  band: string | null;
  bandLabel: string | null;
  dimensions: Record<string, number | null>;
}

interface UserInfo {
  userId: string;
  userEmail: string;
  name?: string;
}

type TimeFilter = '7days' | '30days' | 'all';

const ALL_USERS = 'all';

// Local part of an email, used as a compact display name for charts/tables.
const emailShort = (email?: string): string =>
  email ? email.split('@')[0] : 'Unknown';

// Seconds -> "Xm" or "Xh Ym" for the duration cards.
const fmtDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '0m';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
};

// Tailwind-ish color for a 1-5 average score (mirrors the rubric band ramp).
const scoreColor = (score?: number | null): string => {
  if (score == null) return '#9CA3AF';
  if (score >= 4.5) return BAND_COLORS.standard;
  if (score >= 3.5) return BAND_COLORS.strong;
  if (score >= 2.5) return BAND_COLORS.soft;
  if (score >= 1.5) return BAND_COLORS.at_risk;
  return BAND_COLORS.lost;
};

// --- Small presentational pieces --------------------------------------------
const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
}> = ({ icon, label, value, sub }) => (
  <Card sx={{ flex: '1 1 180px', minWidth: 180 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 1 }}>
        {icon}
        <Typography variant="body2">{label}</Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const ScoreChip: React.FC<{ score: number | null | undefined }> = ({ score }) => (
  <span
    style={{
      display: 'inline-block',
      minWidth: 34,
      textAlign: 'center',
      padding: '2px 8px',
      borderRadius: 6,
      fontWeight: 600,
      fontSize: 13,
      color: '#0A0B0F',
      backgroundColor: scoreColor(score),
      opacity: score == null ? 0.5 : 1,
    }}
  >
    {score == null ? '—' : score.toFixed(1)}
  </span>
);

const Dashboard: React.FC = () => {
  const { isAdmin } = useRoleAccess();
  const { mongoUser, tutorialActiveStepIndex } = useAppSelector((state) => state.auth);
  const { isTutorialCompleted, startTutorial, isRunning: isTutorialRunning } = useTutorial();

  const hasCheckedTutorial = useRef(false);
  const startTutorialRef = useRef(startTutorial);
  startTutorialRef.current = startTutorial;

  const [timeFilter, setTimeFilter] = useState<TimeFilter>('30days');
  const [selectedUserId, setSelectedUserId] = useState<string>(ALL_USERS);
  const [sessionStats, setSessionStats] = useState<SessionStats | null>(null);
  const [salespeople, setSalespeople] = useState<SalespersonScore[]>([]);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Individual view: a non-admin always sees only themselves; an admin sees it
  // when they pick a specific rep from the selector.
  const showIndividual = !isAdmin || selectedUserId !== ALL_USERS;

  const getDateRange = useCallback(() => {
    const now = new Date();
    let dateFrom: string | undefined;
    if (timeFilter === '7days') dateFrom = subDays(now, 7).toISOString();
    else if (timeFilter === '30days') dateFrom = subDays(now, 30).toISOString();
    return { dateFrom, dateTo: now.toISOString() };
  }, [timeFilter]);

  const fetchRubric = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (isAdmin && selectedUserId !== ALL_USERS) params.userId = selectedUserId;

      const res = await apiService.get('/admin/statistics/rubric', params);
      if (res?.success && res.data) {
        setSessionStats(res.data.sessionStats ?? null);
        setSalespeople(res.data.salespeople ?? []);
      } else {
        setSessionStats(null);
        setSalespeople([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch rubric analytics:', err);
      setError(err?.message || 'Failed to load scoring analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, isAdmin, selectedUserId]);

  const fetchHistory = useCallback(async () => {
    try {
      const { dateFrom, dateTo } = getDateRange();
      const params: Record<string, string> = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      // Non-admins are scoped to themselves server-side; admins pass the rep id.
      if (isAdmin && selectedUserId !== ALL_USERS) params.userId = selectedUserId;

      const res = await apiService.get('/admin/statistics/rubric/history', params);
      setHistory(res?.success && res.data ? res.data.history ?? [] : []);
    } catch (err: any) {
      console.error('Failed to fetch rubric history:', err);
      setHistory([]);
    }
  }, [getDateRange, isAdmin, selectedUserId]);

  // Populate the selector from ALL org users (any role, active or not) — not
  // just those who happen to have sessions — since admins also run sales calls.
  const fetchUsers = useCallback(async () => {
    try {
      const res: any = await apiService.get('/admin/users', { limit: '500' });
      const list: any[] = res?.data?.users ?? res?.data ?? [];
      const mapped: UserInfo[] = list
        .filter((u) => u && (u.firebaseUid || u.uid) && u.email)
        .map((u) => ({
          userId: u.firebaseUid || u.uid,
          userEmail: u.email,
          name: u.name,
        }))
        .sort((a, b) =>
          (a.name || a.userEmail).localeCompare(b.name || b.userEmail, undefined, {
            sensitivity: 'base',
          })
        );
      setUsers(mapped);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, []);

  useEffect(() => {
    fetchRubric();
    if (isAdmin) fetchUsers();
    if (showIndividual) fetchHistory();
    else setHistory([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, selectedUserId, isAdmin]);

  // Auto-start the first-run tutorial (preserved from the prior dashboard).
  useEffect(() => {
    if (isTutorialRunning || tutorialActiveStepIndex !== null) return;
    if (!hasCheckedTutorial.current && mongoUser && !isTutorialCompleted) {
      hasCheckedTutorial.current = true;
      setTimeout(() => startTutorialRef.current(), 1000);
    }
  }, [mongoUser, isTutorialCompleted, isTutorialRunning, tutorialActiveStepIndex]);

  const handleUserChange = (e: SelectChangeEvent) => setSelectedUserId(e.target.value);

  // The salesperson whose detail we show in the individual view.
  const individual: SalespersonScore | undefined = salespeople[0];

  // Radar data: the individual's average across the 10 dimensions.
  const radarData = RUBRIC_DIMENSIONS.map((d) => ({
    dimension: d.short,
    score: individual?.dimensions?.[d.key] ?? 0,
  }));

  // Trend data: overall score % per analyzed session over time.
  // Team baseline for the "vs team" delta on the weak-areas panel. Averaged
  // across reps rather than across sessions, so a rep who ran twice as many
  // calls does not pull the team number toward their own.
  const teamAverages = useMemo<Record<string, number>>(() => {
    const totals: Record<string, { sum: number; count: number }> = {};
    salespeople.forEach((rep) => {
      RUBRIC_DIMENSIONS.forEach((d) => {
        const value = rep?.dimensions?.[d.key];
        if (typeof value !== 'number') return;
        totals[d.key] = totals[d.key] || { sum: 0, count: 0 };
        totals[d.key].sum += value;
        totals[d.key].count += 1;
      });
    });
    return Object.fromEntries(
      Object.entries(totals).map(([key, { sum, count }]) => [key, sum / count])
    );
  }, [salespeople]);

  const trendData = history.map((h, i) => ({
    label: h.date ? format(new Date(h.date), 'MMM d') : `#${i + 1}`,
    score: h.scorePercent,
  }));

  // Ranking data (across-org comparison): overall score % per salesperson.
  // Only users with scored calls can be ranked (no score => nothing to rank).
  const rankingData = [...salespeople]
    .filter((s) => s.avgScorePercent != null)
    .sort((a, b) => (b.avgScorePercent ?? 0) - (a.avgScorePercent ?? 0))
    .map((s) => ({ name: emailShort(s.userEmail), score: s.avgScorePercent }));

  // Across-org comparison rows: EVERY user in the system. Users with no analyzed
  // calls in the window are padded with null scores (rendered as "—"). Scored
  // users sort to the top (highest first); everyone else follows alphabetically.
  const scoreByUser = new Map(salespeople.map((s) => [s.userId, s]));
  const comparisonRows: SalespersonScore[] = [
    ...users.map((u) => {
      const scored = scoreByUser.get(u.userId);
      if (scored) return { ...scored, userEmail: scored.userEmail || u.userEmail };
      return {
        userId: u.userId,
        userEmail: u.userEmail,
        sessionsAnalyzed: 0,
        avgScorePercent: null,
        dimensions: {} as Record<string, number>,
      };
    }),
    // Scored users not in the org user list (e.g. removed accounts) still show.
    ...salespeople.filter((s) => !users.some((u) => u.userId === s.userId)),
  ].sort((a, b) => {
    if (a.avgScorePercent != null && b.avgScorePercent != null) {
      return b.avgScorePercent - a.avgScorePercent;
    }
    if (a.avgScorePercent != null) return -1;
    if (b.avgScorePercent != null) return 1;
    return a.userEmail.localeCompare(b.userEmail, undefined, { sensitivity: 'base' });
  });

  return (
    <MainLayout title="Dashboard">
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Discovery Call Scoring
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Session activity and how the team scores across the 10 Lushin Discovery Call Rubric
              dimensions. Scores are 1–5 per dimension; the overall score is the weighted rubric
              percentage.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {(['7days', '30days', 'all'] as TimeFilter[]).map((tf) => (
              <Button
                key={tf}
                size="small"
                variant={timeFilter === tf ? 'contained' : 'outlined'}
                onClick={() => setTimeFilter(tf)}
              >
                {tf === '7days' ? 'Last 7 days' : tf === '30days' ? 'Last 30 days' : 'All time'}
              </Button>
            ))}
            {isAdmin && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="rep-select-label">User</InputLabel>
                <Select
                  labelId="rep-select-label"
                  label="User"
                  value={selectedUserId}
                  onChange={handleUserChange}
                >
                  <MenuItem value={ALL_USERS}>Across Organization</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.userId} value={u.userId}>
                      {u.name ? `${u.name} (${u.userEmail})` : u.userEmail}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Session statistics */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <StatCard
                icon={<AssessmentIcon fontSize="small" />}
                label="Sessions"
                value={sessionStats?.totalSessions ?? 0}
                sub={`${sessionStats?.analyzedSessions ?? 0} analyzed`}
              />
              <StatCard
                icon={<CheckCircleIcon fontSize="small" />}
                label="Avg call score"
                value={sessionStats?.avgScorePercent != null ? `${sessionStats.avgScorePercent}%` : '—'}
                sub="Weighted rubric"
              />
              <StatCard
                icon={<GroupsIcon fontSize="small" />}
                label="Active salespeople"
                value={sessionStats?.activeSalespeople ?? 0}
                sub="With analyzed calls"
              />
              <StatCard
                icon={<AccessTimeIcon fontSize="small" />}
                label="Avg duration"
                value={fmtDuration(sessionStats?.avgDurationSeconds ?? 0)}
                sub={`${fmtDuration(sessionStats?.totalDurationSeconds ?? 0)} total`}
              />
            </Box>

            {/* Empty state when nothing has been analyzed yet */}
            {showIndividual && (sessionStats?.analyzedSessions ?? 0) === 0 ? (
              <Card>
                <CardContent>
                  <Typography variant="h6">No scored calls yet</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Once discovery calls are completed and the post-call analysis runs, their rubric
                    scores will appear here. Try widening the time range.
                  </Typography>
                </CardContent>
              </Card>
            ) : showIndividual ? (
              /* ---------------- Individual salesperson view ---------------- */
              <>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                      {emailShort(individual?.userEmail)} · rubric profile
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Average score across the 10 dimensions ({individual?.sessionsAnalyzed ?? 0}{' '}
                      analyzed calls). Overall:{' '}
                      <b>
                        {individual?.avgScorePercent != null
                          ? `${individual.avgScorePercent}%`
                          : '—'}
                      </b>
                      .
                    </Typography>
                    <Box sx={{ width: '100%', height: 360 }}>
                      <ResponsiveContainer>
                        <RadarChart data={radarData} outerRadius="75%">
                          <PolarGrid />
                          <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11 }} />
                          <PolarRadiusAxis domain={[0, RUBRIC_MAX_SCORE]} tick={{ fontSize: 10 }} />
                          <Radar
                            name="Avg score"
                            dataKey="score"
                            stroke="#00B5A0"
                            fill="#00E5C7"
                            fillOpacity={0.4}
                          />
                          <Tooltip formatter={(v: any) => [`${Number(v).toFixed(1)} / 5`, 'Avg']} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>

                {/* Per-dimension averages as labeled chips */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Dimension averages
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {RUBRIC_DIMENSIONS.map((d) => (
                        <Box
                          key={d.key}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 220 }}
                        >
                          <ScoreChip score={individual?.dimensions?.[d.key] ?? null} />
                          <Typography variant="body2">{d.label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>

                {/* What to do about it, before the charts that show why. */}
                <WeakAreasPanel
                  history={history}
                  teamAverages={teamAverages}
                  subjectLabel={
                    selectedUserId === ALL_USERS
                      ? 'this team'
                      : emailShort(
                          users.find((u) => u.userId === selectedUserId)?.userEmail
                        )
                  }
                />

                <DimensionTrendGrid history={history} />

                {/* Overall score trend over time */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Overall score over time
                    </Typography>
                    {trendData.length > 0 ? (
                      <Box sx={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                          <LineChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                            <Tooltip formatter={(v: any) => [`${v}%`, 'Score']} />
                            <Line
                              type="monotone"
                              dataKey="score"
                              stroke="#00B5A0"
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              connectNulls
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No analyzed calls in this period.
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Recent analyzed sessions */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Analyzed calls
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ textAlign: 'left', color: '#6B6F7A' }}>
                            <th style={{ padding: '8px 12px' }}>Date</th>
                            <th style={{ padding: '8px 12px' }}>Call</th>
                            <th style={{ padding: '8px 12px' }}>Band</th>
                            <th style={{ padding: '8px 12px' }}>Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...history].reverse().map((h) => (
                            <tr key={h.sessionId} style={{ borderTop: '1px solid #eee' }}>
                              <td style={{ padding: '8px 12px' }}>
                                {h.date ? format(new Date(h.date), 'MMM d, yyyy') : '—'}
                              </td>
                              <td style={{ padding: '8px 12px' }}>{h.title || h.sessionId}</td>
                              <td style={{ padding: '8px 12px' }}>
                                {h.bandLabel ? (
                                  <span
                                    style={{
                                      padding: '2px 8px',
                                      borderRadius: 6,
                                      fontSize: 12,
                                      color: '#0A0B0F',
                                      backgroundColor: h.band ? BAND_COLORS[h.band] || '#E5E7EB' : '#E5E7EB',
                                    }}
                                  >
                                    {h.bandLabel}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                                {h.scorePercent != null ? `${h.scorePercent}%` : '—'}
                              </td>
                            </tr>
                          ))}
                          {history.length === 0 && (
                            <tr>
                              <td colSpan={4} style={{ padding: '12px', color: '#6B6F7A' }}>
                                No analyzed calls in this period.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </Box>
                  </CardContent>
                </Card>
              </>
            ) : (
              /* ---------------- Across-team comparison view ---------------- */
              <>
                {/* Overall ranking */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <EmojiEventsIcon fontSize="small" color="action" />
                      <Typography variant="h6">Overall score by salesperson</Typography>
                    </Box>
                    {rankingData.length > 0 ? (
                      <Box sx={{ width: '100%', height: Math.max(220, rankingData.length * 44) }}>
                        <ResponsiveContainer>
                          <BarChart
                            data={rankingData}
                            layout="vertical"
                            margin={{ left: 24, right: 24 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={120}
                              tick={{ fontSize: 12 }}
                            />
                            <Tooltip formatter={(v: any) => [`${v}%`, 'Overall']} />
                            <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                              {rankingData.map((entry) => (
                                <Cell
                                  key={entry.name}
                                  fill={scoreColor((entry.score ?? 0) / 20)}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No scored salespeople in this period.
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Per-dimension comparison matrix */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                      Dimension scores by salesperson
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Average score (1–5) per rubric dimension. Click a salesperson in the selector
                      above to see their trend over time.
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ color: '#6B6F7A' }}>
                            <th style={{ padding: '8px 10px', textAlign: 'left', position: 'sticky', left: 0, background: '#fff' }}>
                              Salesperson
                            </th>
                            <th style={{ padding: '8px 10px' }}>Calls</th>
                            <th style={{ padding: '8px 10px' }}>Overall</th>
                            {RUBRIC_DIMENSIONS.map((d) => (
                              <th key={d.key} style={{ padding: '8px 10px' }} title={d.label}>
                                {d.short}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {comparisonRows.map((s) => {
                            const hasCalls = s.sessionsAnalyzed > 0;
                            return (
                              <tr key={s.userId} style={{ borderTop: '1px solid #eee', opacity: hasCalls ? 1 : 0.6 }}>
                                <td style={{ padding: '8px 10px', fontWeight: 600, position: 'sticky', left: 0, background: '#fff' }}>
                                  {emailShort(s.userEmail)}
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                  {hasCalls ? s.sessionsAnalyzed : '—'}
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>
                                  {s.avgScorePercent != null ? `${s.avgScorePercent}%` : '—'}
                                </td>
                                {RUBRIC_DIMENSIONS.map((d) => (
                                  <td key={d.key} style={{ padding: '6px 8px', textAlign: 'center' }}>
                                    <ScoreChip score={s.dimensions?.[d.key] ?? null} />
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                          {comparisonRows.length === 0 && (
                            <tr>
                              <td colSpan={3 + RUBRIC_DIMENSIONS.length} style={{ padding: '12px', color: '#6B6F7A' }}>
                                No users found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </Box>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </Box>
    </MainLayout>
  );
};

export default Dashboard;
